import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Shield, Layers, TrendingDown, Flame, BarChart3 } from 'lucide-react';
interface CaptiveCapitalReinsuranceProps {
  onNavigate?: (dashboard: string) => void;
}
// Mock Data
const scrCoverage = [{
  name: 'Available Capital',
  value: 15.2
}, {
  name: 'SCR Requirement',
  value: 8.5
}, {
  name: 'MCR Requirement',
  value: 3.2
}];
const reinsuranceUtil = [{
  layer: 'Primary',
  limit: 1000000,
  used: 850000
}, {
  layer: '1st Excess',
  limit: 4000000,
  used: 1200000
}, {
  layer: '2nd Excess',
  limit: 5000000,
  used: 0
}];
const stressScenarios = [{
  name: '1-in-100 Wind',
  impact: 2.5,
  buffer: 6.7
}, {
  name: '1-in-250 Quake',
  impact: 4.2,
  buffer: 5.0
}, {
  name: 'Inflation +10%',
  impact: 1.8,
  buffer: 7.4
}];
const COLORS = {
  primary: '#EC4899',
  secondary: '#9333EA',
  tertiary: '#3B82F6',
  used: '#EF4444',
  unused: '#E5E5E5'
};
export function CaptiveCapitalReinsurance({
  onNavigate
}: CaptiveCapitalReinsuranceProps) {
  return <div className="space-y-6 animate-in" data-id="element-256">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-257">
        <div data-id="element-258">
          <h1 className="text-2xl font-bold text-black" data-id="element-259">
            Capital & Reinsurance
          </h1>
          <p className="text-neutral-500 mt-1" data-id="element-260">
            Solvency position, reinsurance utilisation, and stress testing
          </p>
        </div>
      </div>

      {/* Top Row: KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-261">
        <div className="bg-white p-6 rounded-lg border border-neutral-200 shadow-sm" data-id="element-262">
          <div className="flex justify-between items-start mb-2" data-id="element-263">
            <p className="text-xs font-medium text-neutral-500 uppercase" data-id="element-264">
              SCR Coverage Ratio
            </p>
            <Shield size={20} className="text-pink-500" data-id="element-265" />
          </div>
          <h3 className="text-3xl font-bold text-black" data-id="element-266">178%</h3>
          <p className="text-xs text-green-600 mt-1" data-id="element-267">Well Capitalised</p>
          <p className="text-[10px] text-neutral-400 mt-1" data-id="element-268">
            Available Capital / SCR Requirement
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg border border-neutral-200 shadow-sm" data-id="element-269">
          <div className="flex justify-between items-start mb-2" data-id="element-270">
            <p className="text-xs font-medium text-neutral-500 uppercase" data-id="element-271">
              Retention Burn %
            </p>
            <Flame size={20} className="text-pink-500" data-id="element-272" />
          </div>
          <h3 className="text-3xl font-bold text-black" data-id="element-273">62%</h3>
          <p className="text-xs text-amber-600 mt-1" data-id="element-274">Monitor threshold: 80%</p>
          <p className="text-[10px] text-neutral-400 mt-1" data-id="element-275">
            Incurred losses / Net retention
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg border border-neutral-200 shadow-sm" data-id="element-276">
          <div className="flex justify-between items-start mb-2" data-id="element-277">
            <p className="text-xs font-medium text-neutral-500 uppercase" data-id="element-278">
              Layer Exhaustion Probability
            </p>
            <BarChart3 size={20} className="text-purple-500" data-id="element-279" />
          </div>
          <h3 className="text-3xl font-bold text-black" data-id="element-280">8.5%</h3>
          <p className="text-xs text-green-600 mt-1" data-id="element-281">Within tolerance</p>
          <p className="text-[10px] text-neutral-400 mt-1" data-id="element-282">
            Modelled probability of breaching primary layer
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-283">
        {/* SCR Chart */}
        <div className="bg-white p-6 rounded-lg border border-neutral-200 shadow-sm" data-id="element-284">
          <h3 className="text-lg font-medium text-black mb-4" data-id="element-285">
            Solvency Capital Requirement
          </h3>
          <div className="h-80" data-id="element-286">
            <ResponsiveContainer width="100%" height="100%" data-id="element-287">
              <BarChart data={scrCoverage} layout="vertical" data-id="element-288">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} data-id="element-289" />
                <XAxis type="number" unit="£M" data-id="element-290" />
                <YAxis type="category" dataKey="name" width={150} data-id="element-291" />
                <Tooltip data-id="element-292" />
                <Bar dataKey="value" fill={COLORS.secondary} radius={[0, 4, 4, 0]} data-id="element-293" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Reinsurance Utilisation */}
        <div className="bg-white p-6 rounded-lg border border-neutral-200 shadow-sm" data-id="element-294">
          <h3 className="text-lg font-medium text-black mb-4" data-id="element-295">
            Reinsurance Tower Utilisation
          </h3>
          <div className="space-y-6" data-id="element-296">
            {reinsuranceUtil.map((layer, idx) => {
            const percent = layer.used / layer.limit * 100;
            return <div key={idx} data-id="element-297">
                  <div className="flex justify-between text-sm mb-1" data-id="element-298">
                    <span className="font-medium text-black" data-id="element-299">
                      {layer.layer}
                    </span>
                    <span className="text-neutral-500" data-id="element-300">
                      £{(layer.used / 1000000).toFixed(1)}M / £
                      {(layer.limit / 1000000).toFixed(1)}M
                    </span>
                  </div>
                  <div className="h-4 bg-neutral-100 rounded-full overflow-hidden" data-id="element-301">
                    <div className="h-full bg-pink-500 transition-all duration-500" style={{
                  width: `${percent}%`
                }} data-id="element-302" />
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>

      {/* Stress Scenarios */}
      <div className="bg-white p-6 rounded-lg border border-neutral-200 shadow-sm" data-id="element-303">
        <h3 className="text-lg font-medium text-black mb-4" data-id="element-304">
          Stress Test Scenarios
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-305">
          {stressScenarios.map((scenario, idx) => <div key={idx} className="p-4 border border-neutral-200 rounded-lg" data-id="element-306">
              <h4 className="font-bold text-black mb-2" data-id="element-307">{scenario.name}</h4>
              <div className="space-y-2" data-id="element-308">
                <div className="flex justify-between text-sm" data-id="element-309">
                  <span className="text-neutral-500" data-id="element-310">Capital Impact</span>
                  <span className="text-red-600 font-medium" data-id="element-311">
                    -£{scenario.impact}M
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-312">
                  <span className="text-neutral-500" data-id="element-313">Remaining Buffer</span>
                  <span className="text-green-600 font-medium" data-id="element-314">
                    £{scenario.buffer}M
                  </span>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-neutral-100" data-id="element-315">
                <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full" data-id="element-316">
                  Resilient
                </span>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}